/**
 * Item model: stackable catalog items (materials, consumables, battle items,
 * books, gems, cards, chests) and unique equipment instances (gear,
 * accessories, rune stones).
 */

export type Rarity = 'normal' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'relic' | 'ancient' | 'mythic';
export const RARITIES: readonly Rarity[] = ['normal', 'uncommon', 'rare', 'epic', 'legendary', 'relic', 'ancient', 'mythic'];
export const RARITY_COLOR: Record<Rarity, string> = {
  normal: '#ffffff',
  uncommon: '#8df901',
  rare: '#00b0fa',
  epic: '#ce43fc',
  legendary: '#f99200',
  relic: '#fa5d00',
  ancient: '#e3c7a1',
  mythic: '#3cf2e6',
};
export const RARITY_HUE: Record<Rarity, number> = {
  normal: 0x8a8a8a,
  uncommon: 0x5c8f1e,
  rare: 0x1f6fa3,
  epic: 0x8a2fb0,
  legendary: 0xc77a0a,
  relic: 0xc9510b,
  ancient: 0xcdb88a,
  mythic: 0x39c9c1,
};
export const rarityIndex = (r: Rarity): number => RARITIES.indexOf(r);

export type GearSlot = 'weapon' | 'head' | 'shoulders' | 'chest' | 'pants' | 'gloves';
export const GEAR_SLOTS: readonly GearSlot[] = ['weapon', 'head', 'shoulders', 'chest', 'pants', 'gloves'];
export type AccSlot = 'necklace' | 'earring1' | 'earring2' | 'ring1' | 'ring2';
export const ACC_SLOTS: readonly AccSlot[] = ['necklace', 'earring1', 'earring2', 'ring1', 'ring2'];
export type EquipSlot = GearSlot | AccSlot | 'stone';
export type CombatStat = 'crit' | 'spec' | 'swift' | 'dom' | 'endur' | 'expert';
export const COMBAT_STATS: readonly CombatStat[] = ['crit', 'spec', 'swift', 'dom', 'endur', 'expert'];
export const COMBAT_STAT_NAMES: Record<CombatStat, string> = {
  crit: 'Crit',
  spec: 'Specialization',
  swift: 'Swiftness',
  dom: 'Domination',
  endur: 'Endurance',
  expert: 'Expertise',
};

export type ItemKind =
  | 'material'
  | 'consumable'
  | 'battle'
  | 'book'
  | 'skillpotion'
  | 'gem'
  | 'card'
  | 'chest'
  | 'quest'
  | 'token'
  | 'food'
  | 'junk'
  | 'tool';

export type BattleCategory = 'potion' | 'grenade' | 'bomb' | 'utility';

export interface ItemDef {
  id: string;
  name: string;
  kind: ItemKind;
  rarity: Rarity;
  description: string;
  stack: number;
  /** Silver received when sold. */
  value: number;
  icon: { hue: number; glyph: string };
  battle?: { category: BattleCategory; skill: string; cooldown: number; limit: number };
  /** Consumable effect id handled by the game layer. */
  use?: string;
  /** For books: sigil id; gems: type/level; cards: card id; chests: loot table. */
  ref?: string;
  level?: number;
}

const g = (hue: number, glyph: string) => ({ hue, glyph });
const def = (d: Omit<ItemDef, 'stack' | 'value'> & Partial<Pick<ItemDef, 'stack' | 'value'>>): ItemDef => ({ stack: 999, value: 5, ...d });

/** Stackable item catalog. Gear/accessories/stones are instances, not listed here. */
export const ITEMS: Record<string, ItemDef> = {};
function add(...defs: ItemDef[]): void {
  for (const d of defs) ITEMS[d.id] = d;
}

// Tempering materials.
add(
  def({ id: 'aether_shard', name: 'Aether Shards', kind: 'material', rarity: 'uncommon', description: 'Crystallised Lantern-light. Feeds the tempering of any gear.', icon: g(0x5ab8e0, 'star'), value: 1 }),
  def({ id: 'ore_weapon', name: 'Emberforge Ore', kind: 'material', rarity: 'rare', description: 'Dense, heat-veined ore used to temper weapons.', icon: g(0xff7a3a, 'impact'), value: 2 }),
  def({ id: 'ore_armor', name: 'Bulwark Ore', kind: 'material', rarity: 'rare', description: 'Resilient ore used to temper armour.', icon: g(0x7aa0d0, 'shield'), value: 2 }),
  def({ id: 'leap_crystal', name: 'Leap Crystal', kind: 'material', rarity: 'epic', description: 'A crystal that lets tempered gear leap to the next stage.', icon: g(0xc070ff, 'charge'), value: 10 }),
  def({ id: 'fusion_alloy', name: 'Fusion Alloy', kind: 'material', rarity: 'epic', description: 'Crafted at Haven Isle. Required to temper gear beyond +5.', icon: g(0xe0b050, 'buff'), value: 20 }),
  def({ id: 'catalyst', name: 'Tempering Catalyst', kind: 'material', rarity: 'legendary', description: 'Adds +10% of the base success chance per catalyst (up to 10 per attempt).', icon: g(0xffd040, 'bolt'), value: 40 }),
  def({ id: 'forge_tome', name: 'Forgemaster Tome', kind: 'material', rarity: 'legendary', description: 'Doubles the base success chance of a +11 to +15 attempt.', icon: g(0xffa030, 'note'), value: 80, stack: 99 }),
);

// Life skill materials.
add(
  def({ id: 'herb', name: 'Wild Herb', kind: 'material', rarity: 'normal', description: 'Common herb gathered by foraging.', icon: g(0x6ad060, 'buff'), value: 3 }),
  def({ id: 'herb_rare', name: 'Moonpetal', kind: 'material', rarity: 'rare', description: 'A rare glowing flower.', icon: g(0xa0c0ff, 'buff'), value: 18 }),
  def({ id: 'timber', name: 'Timber', kind: 'material', rarity: 'normal', description: 'Sturdy logs from logging.', icon: g(0x9a6a3a, 'lance'), value: 3 }),
  def({ id: 'timber_rare', name: 'Heartwood', kind: 'material', rarity: 'rare', description: 'Dense resin-rich heartwood.', icon: g(0xc08040, 'lance'), value: 18 }),
  def({ id: 'ore_raw', name: 'Iron Ore', kind: 'material', rarity: 'normal', description: 'Raw ore from mining.', icon: g(0x8a8a9a, 'impact'), value: 3 }),
  def({ id: 'ore_rare', name: 'Starsteel Nugget', kind: 'material', rarity: 'rare', description: 'Meteoric metal.', icon: g(0x9ad0ff, 'impact'), value: 20 }),
  def({ id: 'fish', name: 'Silver Trout', kind: 'material', rarity: 'normal', description: 'A plump river fish.', icon: g(0x7ab8e0, 'orb'), value: 4 }),
  def({ id: 'fish_rare', name: 'Gleamfin', kind: 'material', rarity: 'rare', description: 'A fish that glows faintly blue.', icon: g(0x3cf2e6, 'orb'), value: 24 }),
  def({ id: 'relic_fragment', name: 'Relic Fragment', kind: 'material', rarity: 'uncommon', description: 'Ancient debris unearthed by excavating.', icon: g(0xd0a060, 'star'), value: 6 }),
  def({ id: 'relic_rare', name: 'Pristine Relic', kind: 'material', rarity: 'epic', description: 'A remarkably preserved artefact.', icon: g(0xe0c080, 'star'), value: 40 }),
  def({ id: 'hide', name: 'Thick Hide', kind: 'material', rarity: 'normal', description: 'Hide from hunted beasts.', icon: g(0xa07050, 'claw'), value: 3 }),
);

// Activity tokens.
add(
  def({ id: 'rift_sigil', name: 'Rift Sigil', kind: 'token', rarity: 'rare', description: 'Earned in the Rift Delve. Spend at the rift scholar.', icon: g(0xd070ff, 'spiral'), value: 0 }),
  def({ id: 'colossus_mark', name: 'Colossus Mark', kind: 'token', rarity: 'epic', description: 'Proof of a Colossus Hunt.', icon: g(0xff9040, 'claw'), value: 0 }),
  def({ id: 'vault_token', name: 'Reliquary Seal', kind: 'token', rarity: 'epic', description: 'Exchanged for Deepvault set gear.', icon: g(0x40c0b0, 'shield'), value: 0 }),
  def({ id: 'warlord_relic', name: "Warlord's Relic", kind: 'token', rarity: 'legendary', description: 'Taken from the Hollow Throne. Crafts the Warlord set.', icon: g(0xff4030, 'star'), value: 0 }),
  def({ id: 'bounty_seal', name: 'Bounty Seal', kind: 'token', rarity: 'uncommon', description: 'Reputation with the Bounty Board.', icon: g(0xd04050, 'note'), value: 0 }),
  def({ id: 'arena_crest', name: 'Crucible Crest', kind: 'token', rarity: 'rare', description: 'Won in the Crucible.', icon: g(0xff6040, 'shield'), value: 0 }),
);

// Consumables (field) and battle items (raid-usable, limited per instance).
add(
  def({ id: 'potion_small', name: 'Healing Draught', kind: 'battle', rarity: 'uncommon', description: 'Restores 30% of max HP.', icon: g(0xff4040, 'buff'), value: 10, battle: { category: 'potion', skill: 'bi_potion_small', cooldown: 10, limit: 5 } }),
  def({ id: 'potion_major', name: 'Greater Healing Draught', kind: 'battle', rarity: 'rare', description: 'Restores 45% of max HP.', icon: g(0xff2020, 'buff'), value: 30, battle: { category: 'potion', skill: 'bi_potion_major', cooldown: 10, limit: 7 } }),
  def({ id: 'potion_elixir', name: 'Radiant Elixir', kind: 'battle', rarity: 'epic', description: 'Restores 60% of max HP.', icon: g(0xff90a0, 'buff'), value: 80, battle: { category: 'potion', skill: 'bi_potion_elixir', cooldown: 10, limit: 9 } }),
  def({ id: 'grenade_flame', name: 'Flame Grenade', kind: 'battle', rarity: 'rare', description: 'Fire damage and a burning field for 10 s.', icon: g(0xff6a2a, 'impact'), value: 20, battle: { category: 'grenade', skill: 'bi_flame', cooldown: 30, limit: 3 } }),
  def({ id: 'grenade_frost', name: 'Frost Grenade', kind: 'battle', rarity: 'rare', description: 'Water damage; freezes non-boss foes for 4 s.', icon: g(0x7ad8ff, 'impact'), value: 20, battle: { category: 'grenade', skill: 'bi_frost', cooldown: 30, limit: 3 } }),
  def({ id: 'grenade_flash', name: 'Flash Grenade', kind: 'battle', rarity: 'rare', description: 'Holy damage; stuns non-boss foes for 4 s. High stagger.', icon: g(0xffe28a, 'star'), value: 20, battle: { category: 'grenade', skill: 'bi_flash', cooldown: 30, limit: 3 } }),
  def({ id: 'grenade_whirl', name: 'Whirlwind Grenade', kind: 'battle', rarity: 'epic', description: 'Launches foes. Very high stagger — the tool for stagger checks.', icon: g(0x9affc8, 'spiral'), value: 30, battle: { category: 'grenade', skill: 'bi_whirl', cooldown: 30, limit: 3 } }),
  def({ id: 'grenade_dark', name: 'Shade Grenade', kind: 'battle', rarity: 'epic', description: 'Dark damage; target defence -20% for 20 s.', icon: g(0x9a50ff, 'impact'), value: 30, battle: { category: 'grenade', skill: 'bi_dark', cooldown: 30, limit: 3 } }),
  def({ id: 'grenade_clay', name: 'Clay Grenade', kind: 'battle', rarity: 'rare', description: 'Attack and move speed -40% for 20 s.', icon: g(0xa48a5c, 'impact'), value: 20, battle: { category: 'grenade', skill: 'bi_clay', cooldown: 30, limit: 3 } }),
  def({ id: 'bomb_destruction', name: 'Sundering Bomb', kind: 'battle', rarity: 'epic', description: 'Weak Point Lv. 3: breaks shells and parts.', icon: g(0xffb040, 'impact'), value: 30, battle: { category: 'bomb', skill: 'bi_destruction', cooldown: 30, limit: 5 } }),
  def({ id: 'bomb_corrosive', name: 'Corrosive Bomb', kind: 'battle', rarity: 'rare', description: 'Weak point damage +1 level for 20 s.', icon: g(0x8fbf3a, 'impact'), value: 20, battle: { category: 'bomb', skill: 'bi_corrosive', cooldown: 30, limit: 5 } }),
  def({ id: 'bomb_sleep', name: 'Slumber Bomb', kind: 'battle', rarity: 'rare', description: 'Puts non-boss foes to sleep for 12 s (breaks on damage).', icon: g(0xb5a4ff, 'orb'), value: 20, battle: { category: 'bomb', skill: 'bi_sleep', cooldown: 30, limit: 3 } }),
  def({ id: 'potion_timestop', name: 'Stillwater Tonic', kind: 'battle', rarity: 'epic', description: 'Invulnerable for 3 s (not against wipe mechanics).', icon: g(0xe0e8ff, 'shield'), value: 60, battle: { category: 'utility', skill: 'bi_timestop', cooldown: 30, limit: 3 } }),
  def({ id: 'potion_panacea', name: 'Panacea', kind: 'battle', rarity: 'rare', description: 'Removes all harmful effects.', icon: g(0x9aff9a, 'buff'), value: 20, battle: { category: 'utility', skill: 'bi_panacea', cooldown: 30, limit: 3 } }),
  def({ id: 'potion_protect', name: 'Aegis Tonic', kind: 'battle', rarity: 'rare', description: 'Shield of 15% max HP for 8 s.', icon: g(0xbfe0ff, 'shield'), value: 20, battle: { category: 'utility', skill: 'bi_protect', cooldown: 30, limit: 3 } }),
  def({ id: 'flag_march', name: 'Marching Banner', kind: 'battle', rarity: 'rare', description: 'Party move speed +20% for 20 s.', icon: g(0x9affc8, 'buff'), value: 20, battle: { category: 'utility', skill: 'bi_march', cooldown: 30, limit: 3 } }),
  def({ id: 'campfire_kit', name: 'Campfire Kit', kind: 'battle', rarity: 'uncommon', description: 'Party HP/MP regeneration zone for 30 s.', icon: g(0xffa040, 'star'), value: 10, battle: { category: 'utility', skill: 'bi_campfire', cooldown: 60, limit: 1 } }),
);

// Skill point potions, books, chests and misc.
add(
  def({ id: 'skill_potion', name: 'Tonic of Insight', kind: 'skillpotion', rarity: 'legendary', description: 'Permanently grants +2 skill points to this character.', icon: g(0xffd060, 'charge'), value: 0, stack: 99, use: 'skillPoints:2' }),
  def({ id: 'phoenix_plume', name: 'Phoenix Plume', kind: 'consumable', rarity: 'rare', description: 'Revive on the spot after falling (field content).', icon: g(0xff8040, 'buff'), value: 15, stack: 99 }),
  def({ id: 'song_return', name: 'Homeward Refrain', kind: 'consumable', rarity: 'rare', description: 'Return to Havenreach.', icon: g(0x9ad0ff, 'note'), value: 0, stack: 1, use: 'returnHome' }),
  def({ id: 'rift_energy_tonic', name: 'Rift Tonic', kind: 'consumable', rarity: 'epic', description: 'Restores 50 Rift Energy.', icon: g(0xd070ff, 'bolt'), value: 0, stack: 99, use: 'riftEnergy:50' }),
);

// Gems: damage (+3%/level) and cooldown (-2%/level) for one skill; fuse 3 → 1 higher.
for (let lvl = 1; lvl <= 7; lvl++) {
  const rarity: Rarity = (['uncommon', 'rare', 'rare', 'epic', 'epic', 'legendary', 'relic'] as const)[lvl - 1] ?? 'rare';
  add(
    def({ id: `gem_dmg_${lvl}`, name: `Lv.${lvl} Ember Gem`, kind: 'gem', rarity, description: `Socket into a skill: damage +${3 * lvl}%.`, icon: g(0xff5a3a, 'star'), value: 20 * lvl * lvl, stack: 99, ref: 'dmg', level: lvl }),
    def({ id: `gem_cd_${lvl}`, name: `Lv.${lvl} Tide Gem`, kind: 'gem', rarity, description: `Socket into a skill: cooldown -${2 * lvl}%.`, icon: g(0x3a9aff, 'star'), value: 20 * lvl * lvl, stack: 99, ref: 'cd', level: lvl }),
  );
}

// Sigil books: reading one teaches 5 points of a sigil (max 20 learned).
export const SIGIL_BOOK_IDS: string[] = [];
export function registerSigilBooks(sigils: { id: string; name: string }[]): void {
  for (const s of sigils) {
    const id = `book_${s.id}`;
    if (ITEMS[id]) continue;
    SIGIL_BOOK_IDS.push(id);
    add(def({ id, name: `Codex: ${s.name}`, kind: 'book', rarity: 'epic', description: `Read to learn 5 points of the ${s.name} sigil (max 20).`, icon: g(0xb080ff, 'note'), value: 50, stack: 99, ref: s.id }));
  }
}

/** Returns a catalog item, throwing on unknown ids (content bugs should fail loudly in tests). */
export function itemDef(id: string): ItemDef {
  const d = ITEMS[id];
  if (!d) throw new Error(`Unknown item '${id}'`);
  return d;
}

export function registerItems(defs: ItemDef[]): void {
  add(...defs);
}

// ---------------------------------------------------------------- equipment instances

export interface GearItem {
  uid: string;
  kind: 'gear';
  slot: GearSlot;
  name: string;
  rarity: Rarity;
  /** Item level before tempering. */
  baseIlvl: number;
  /** Tempering level +0..+15. */
  plus: number;
  maxPlus: number;
  quality: number;
  set?: string;
  /** Tempering pity state. */
  fails: number;
  resolve: number;
  bound: boolean;
}

export interface AccessoryItem {
  uid: string;
  kind: 'accessory';
  slot: 'necklace' | 'earring' | 'ring';
  name: string;
  rarity: Rarity;
  quality: number;
  ilvl: number;
  stats: Partial<Record<CombatStat, number>>;
  /** Sigil points granted while equipped. */
  sigils: { id: string; pts: number }[];
  bound: boolean;
}

export interface StoneLine {
  sigil: string;
  negative: boolean;
  /** Node results: true = success, false = fail, null = not etched yet. */
  nodes: (boolean | null)[];
}

export interface StoneItem {
  uid: string;
  kind: 'stone';
  name: string;
  rarity: Rarity;
  /** Current etching success chance (0.25..0.75). */
  chance: number;
  lines: StoneLine[];
  bound: boolean;
}

export type EquipItem = GearItem | AccessoryItem | StoneItem;

export function gearIlvl(g: GearItem): number {
  return g.baseIlvl + g.plus * (g.maxPlus >= 15 ? 10 : 5);
}

export function stonePoints(line: StoneLine): number {
  return line.nodes.filter((n) => n === true).length;
}

/** Names for generated gear by slot and tier. */
const SLOT_NOUNS: Record<GearSlot, string[]> = {
  weapon: ['Blade', 'Arms', 'Armament'],
  head: ['Helm', 'Crown', 'Hood'],
  shoulders: ['Pauldrons', 'Mantle', 'Spaulders'],
  chest: ['Cuirass', 'Vestment', 'Hauberk'],
  pants: ['Greaves', 'Legguards', 'Tassets'],
  gloves: ['Gauntlets', 'Grips', 'Handguards'],
};
const TIER_ADJ = ['Worn', 'Traveller', 'Vale', 'Ridgeguard', 'Sunscarred', 'Duneborn', 'Rimebound', 'Frostforged', 'Lanternbound'];

export function gearName(slot: GearSlot, ilvl: number, rarity: Rarity, set?: string): string {
  if (set) return `${SET_NAMES[set] ?? set} ${SLOT_NOUNS[slot][0]}`;
  const adj = TIER_ADJ[Math.min(TIER_ADJ.length - 1, Math.floor(ilvl / 30))] as string;
  const noun = SLOT_NOUNS[slot][rarityIndex(rarity) % 3] as string;
  return `${adj} ${noun}`;
}

export const SET_NAMES: Record<string, string> = {
  vanguard: 'Vanguard',
  reliquary: 'Reliquary',
  warlord: 'Hollowbane',
};

export interface SetBonus {
  pieces: number;
  text: string;
  mods: { dmg?: number; crit?: number; critDmg?: number; hp?: number; atkSpeed?: number; dmgReduction?: number; backHead?: number };
}

export const SET_BONUSES: Record<string, SetBonus[]> = {
  vanguard: [{ pieces: 2, text: 'Max HP +5%', mods: { hp: 0.05 } }, { pieces: 6, text: 'Damage +4%', mods: { dmg: 0.04 } }],
  reliquary: [{ pieces: 2, text: 'Crit rate +8%', mods: { crit: 0.08 } }, { pieces: 4, text: 'Crit damage +20%', mods: { critDmg: 0.2 } }, { pieces: 6, text: 'Damage +8%', mods: { dmg: 0.08 } }],
  warlord: [{ pieces: 2, text: 'Damage +6%', mods: { dmg: 0.06 } }, { pieces: 4, text: 'Back & head attack damage +10%', mods: { backHead: 0.1 } }, { pieces: 6, text: 'Damage +12%, attack speed +6%', mods: { dmg: 0.12, atkSpeed: 0.06 } }],
};

let uidCounter = 0;
export function newUid(prefix = 'it'): string {
  uidCounter = (uidCounter + 1) % 1e6;
  return `${prefix}_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}_${uidCounter}`;
}

export function makeGear(slot: GearSlot, ilvl: number, rarity: Rarity, opts: { set?: string; quality?: number; maxPlus?: number; plus?: number } = {}): GearItem {
  return {
    uid: newUid('gear'),
    kind: 'gear',
    slot,
    name: gearName(slot, ilvl, rarity, opts.set),
    rarity,
    baseIlvl: Math.round(ilvl),
    plus: opts.plus ?? 0,
    maxPlus: opts.maxPlus ?? (opts.set ? 15 : 5),
    quality: opts.quality ?? 50,
    set: opts.set,
    fails: 0,
    resolve: 0,
    bound: true,
  };
}
